import {
  fetchAllAttachments,
  updateAllAttachments,
  updateAttachmentInputSelectionError,
  updateAttachmentUploadError,
  updateAttachmentUploadProgress,
  updateAttachmentUploadQueue,
  updateAttachmentUploadQueueCurentJobIndex,
  updateAttachmentUploadQueueTotalJobs,
} from 'actions/attachments';
import { showErrorBar } from 'actions/errorBar';
import { MAX_ATTACHMENTS_PER_VIDEO } from 'constants/attachments';
import { ERROR } from 'constants/error-severities';
import { ATTACHMENT_CREATED, ATTACHMENT_DELETED } from 'constants/events';
import { MAX_ATTACHMENT_SIZE_IN_BYTES } from 'constants/runtimeConfig';
import * as analytics from 'utilities/analytics';
import {
  queueUploadAttachment,
  removeFromUploadQueue,
  runUploadQueue,
} from 'utilities/attachments';
import { formatBytes } from 'utilities/bytes';
import { getFileHash, getFileMime } from 'utilities/file';
import { getGraphQLClient } from 'utilities/graphql';
import * as sentry from 'utilities/sentry';

import CompleteUploadVideoAttachment from './CompleteUploadVideoAttachment.graphql';
import CreateLinkAttachmentForVideo from './CreateLinkAttachmentForVideo.graphql';
import CreateUploadAttachmentForVideo from './CreateUploadAttachmentForVideo.graphql';
import DeleteVideoAttachment from './DeleteVideoAttachment.graphql';
import GetAttachmentsForVideo from './GetAttachmentsForVideo.graphql';

function conditionallyResetUploadQueueState(dispatch, getState) {
  const { upload_queue } = getState().attachments;

  if (upload_queue.length === 0) {
    dispatch(updateAttachmentUploadProgress(0));
    dispatch(updateAttachmentUploadQueueCurentJobIndex(0));
    dispatch(updateAttachmentUploadQueueTotalJobs(0));
  }
}

function getAttachmentEventTrackProperties(attachment, getState) {
  const {
    type: attachment_type,
    url: attachment_url,
    mime,
    name,
    owner_id,
    video_id,
  } = attachment || {};
  const { current_user_is_owner: is_video_owner } = getState().share_video;

  return {
    attachment_type,
    attachment_url,
    video_id,
    is_video_owner,
    mime,
    name,
    owner_id,
  };
}

export function deleteAttachmentCreator(attachmentId) {
  return async (dispatch, getState) => {
    try {
      const {
        upload_queue,
        upload_queue_current_job_index,
        upload_queue_total_jobs,
      } = getState().attachments;

      removeFromUploadQueue(attachmentId);
      dispatch(
        updateAttachmentUploadQueue([
          ...upload_queue.filter(
            uploadAttachment => uploadAttachment.id !== attachmentId
          ),
        ])
      );
      dispatch(
        updateAttachmentUploadQueueCurentJobIndex(
          upload_queue_current_job_index - 1
        )
      );
      dispatch(
        updateAttachmentUploadQueueTotalJobs(upload_queue_total_jobs - 1)
      );

      conditionallyResetUploadQueueState(dispatch, getState);

      const client = getGraphQLClient();

      await client.mutate({
        variables: { attachmentId },
        mutation: DeleteVideoAttachment,
      });

      const { attachments } = getState().attachments;
      const attachmentToDelete =
        attachments.find(attachment => attachment.id === attachmentId) || {};

      dispatch(
        updateAllAttachments(
          attachments.filter(attachment => attachment.id !== attachmentId)
        )
      );

      analytics.track(
        ATTACHMENT_DELETED,
        getAttachmentEventTrackProperties(attachmentToDelete, getState)
      );
    } catch (err) {
      sentry.logException(err, 'Oops! Error deleting attachment');
      dispatch(showErrorBar(ERROR, 'Oops! Error deleting attachment'));
    }
  };
}

export function fetchAllAttachmentsCreator() {
  return async (dispatch, getState) => {
    try {
      const { password, video } = getState().share_video;

      dispatch(fetchAllAttachments(true));

      const client = getGraphQLClient();

      const attachments = await client
        .query({
          variables: { videoId: video.id, password },
          query: GetAttachmentsForVideo,
        })
        .then(({ data }) => data.getAttachmentsForVideo);

      dispatch(updateAllAttachments(attachments));
      dispatch(fetchAllAttachments(false));
    } catch (err) {
      sentry.logException(err, 'Oops! Error fetching video attachments');
      dispatch(showErrorBar(ERROR, 'Oops! Error fetching video attachments'));
      dispatch(fetchAllAttachments(false));
    }
  };
}

const MAX_ATTACHMENT_SIZE_ERROR = `Oh no your file is too large! Try uploading something smaller than ${formatBytes(
  MAX_ATTACHMENT_SIZE_IN_BYTES
)}`;
const MAX_NUM_ATTACHMENTS_ERROR = `You have reached your max! You can only have up to ${MAX_ATTACHMENTS_PER_VIDEO} attachments per video.`;

export function createUploadAttachmentCreator(file) {
  return async (dispatch, getState) => {
    try {
      const { attachments, createWithTimestamp } = getState().attachments;

      if (file.size > MAX_ATTACHMENT_SIZE_IN_BYTES) {
        return dispatch(
          updateAttachmentInputSelectionError(MAX_ATTACHMENT_SIZE_ERROR)
        );
      } else if (attachments.length >= MAX_ATTACHMENTS_PER_VIDEO) {
        return dispatch(
          updateAttachmentInputSelectionError(MAX_NUM_ATTACHMENTS_ERROR)
        );
      }

      dispatch(updateAttachmentInputSelectionError(null));

      const {
        video: { id: videoId },
      } = getState().share_video;
      const { current_time: timestamp } = getState().video_player;

      const mime = (await getFileMime(file)) || file.type || '';
      const fileHash = (await getFileHash(file)).slice(0, 32);
      const name = file.name;

      const variables = { fileHash, mime, name, videoId };

      if (createWithTimestamp) {
        variables.videoTimestamp = Math.round(timestamp);
      }

      const client = getGraphQLClient();

      const { attachment, credentials, key } = await client
        .mutate({
          mutation: CreateUploadAttachmentForVideo,
          variables,
        })
        .then(({ data }) => data.createUploadAttachmentForVideo);

      const newAttachments = [attachment, ...attachments].sort(
        attachment => attachment.video_timestamp
      );

      dispatch(updateAllAttachments(newAttachments));

      // if the upload is complete, that means we've attached a pre-existing
      // s3 attachment to this model - no need to upload :-)
      if (attachment.upload_complete) {
        return;
      }

      const { upload_queue, upload_queue_total_jobs } = getState().attachments;

      dispatch(updateAttachmentUploadQueue([...upload_queue, attachment]));
      dispatch(
        updateAttachmentUploadQueueTotalJobs(upload_queue_total_jobs + 1)
      );

      queueUploadAttachment({
        attachment,
        file,
        credentials,
        key,
        onError: err => {
          sentry.logException(err, 'Oops! Error uploading attachment');
          dispatch(updateAttachmentUploadProgress(0));
          dispatch(updateAttachmentUploadError(err));
        },
        onProgress: progress => {
          dispatch(updateAttachmentUploadProgress(progress));
        },
        onSuccess: () => {
          analytics.track(
            ATTACHMENT_CREATED,
            getAttachmentEventTrackProperties(attachment, getState)
          );
          dispatch(updateAttachmentUploadProgress(1));
          dispatch(completeUploadAttachmentCreator(attachment.id));

          window.setTimeout(() => {
            const { upload_queue } = getState().attachments;

            dispatch(
              updateAttachmentUploadQueue([
                ...upload_queue.filter(
                  uploadAttachment => uploadAttachment.id !== attachment.id
                ),
              ])
            );
            runUploadQueue();
            conditionallyResetUploadQueueState(dispatch, getState);
          }, 3000);
        },
        onStart: () => {
          const { upload_queue_current_job_index } = getState().attachments;

          dispatch(
            updateAttachmentUploadQueueCurentJobIndex(
              upload_queue_current_job_index + 1
            )
          );
          dispatch(updateAttachmentUploadProgress(0));
          dispatch(updateAttachmentUploadError(null));
        },
      });
    } catch (err) {
      sentry.logException(err, 'Oops! Error uploading attachment');
      dispatch(showErrorBar(ERROR, 'Oops! Error uploading attachment'));
    }
  };
}

export function createLinkAttachmentCreator(url) {
  return async (dispatch, getState) => {
    try {
      const { attachments, createWithTimestamp } = getState().attachments;

      if (attachments.length >= MAX_ATTACHMENTS_PER_VIDEO) {
        return dispatch(
          updateAttachmentInputSelectionError(MAX_NUM_ATTACHMENTS_ERROR)
        );
      }

      dispatch(updateAttachmentInputSelectionError(null));

      const {
        video: { id: videoId },
      } = getState().share_video;
      const { current_time: timestamp } = getState().video_player;

      let finalUrl = url;

      if (!/^http(s)?/i.test(finalUrl)) {
        finalUrl = `https://${url}`;
      }

      const variables = {
        name: url,
        url: finalUrl,
        videoId,
      };

      if (createWithTimestamp) {
        variables.videoTimestamp = Math.round(timestamp);
      }

      const client = getGraphQLClient();

      const attachment = await client
        .mutate({
          mutation: CreateLinkAttachmentForVideo,
          variables,
        })
        .then(({ data }) => data.createLinkAttachmentForVideo);

      const newAttachments = [attachment, ...attachments].sort(
        attachment => attachment.video_timestamp
      );

      dispatch(updateAllAttachments(newAttachments));

      analytics.track(
        ATTACHMENT_CREATED,
        getAttachmentEventTrackProperties(attachment, getState)
      );
    } catch (err) {
      sentry.logException(err, 'Oops! Error creating link attachment');
      dispatch(showErrorBar(ERROR, 'Oops! Error creating link attachment'));
    }
  };
}

export function completeUploadAttachmentCreator(attachmentId) {
  return async (dispatch, getState) => {
    try {
      const client = getGraphQLClient();
      const completedAttachment = await client
        .mutate({
          mutation: CompleteUploadVideoAttachment,
          variables: { attachmentId },
        })
        .then(({ data }) => data.completeUploadVideoAttachment);

      const { attachments } = getState().attachments;

      const newAttachments = [
        completedAttachment,
        ...attachments.filter(attachment => attachment.id !== attachmentId),
      ];

      dispatch(updateAllAttachments(newAttachments));
    } catch (err) {
      sentry.logException(err, 'Oops! Error completing upload attachment');
      dispatch(showErrorBar(ERROR, 'Oops! Error completing upload attachment'));
    }
  };
}
